import { useMutation } from '@apollo/client';
import React, { ReactNode } from 'react';
import { useCollapse } from 'react-collapsed';
import styled from 'styled-components';
import KubitTooltip from '../../../../components/KubitTooltip';
import { useAppDispatch } from '../../../../redux/hooks';
import { useUserSettingsSelector } from '../../../../redux/selectorHooks';
import { setDashboardSidebarSetting } from '../../../../redux/slices/userSettings';
import {
  IUserSettingUpdateMutation,
  IUserSettingUpdateMutationVariables,
  UserSettingKind,
} from '../../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { USER_SETTING_UPDATE } from '../../../../utils/apollo/userSettings';
import { DashboardNavSectionType } from '../../types';
import { dashboardSidebarSettingSchema } from './validation';
import { theme } from '../../../../styles/theme';
import { StyledNavItem } from '../../../../components/SecondaryNav/SecondaryNavUI';

interface IProps {
  variant: DashboardNavSectionType;
  headerComponent: ReactNode;
  children: ReactNode;
  title: string;
  actions?: ReactNode;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;
const Title = styled.div`
  margin-right: auto;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;
const CollapseContent = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;

const CollapseSection = ({
  variant,
  headerComponent,
  children,
  title,
  actions,
}: IProps) => {
  const dispatch = useAppDispatch();
  const dashboardSidebarSetting = useUserSettingsSelector(
    'dashboardSidebarSetting',
  );

  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded: dashboardSidebarSetting?.[variant]?.isExpanded ?? true,
    hasDisabledAnimation: true, // no animation
  });

  const [saveSettings] = useMutation<
    IUserSettingUpdateMutation,
    IUserSettingUpdateMutationVariables
  >(USER_SETTING_UPDATE);

  const onToggleSection = async () => {
    const newSettings = {
      ...(dashboardSidebarSetting ?? {}),
      [variant]: {
        isExpanded: !isExpanded,
      },
    };

    const safeParse = dashboardSidebarSettingSchema.safeParse(newSettings);

    if (safeParse.success) {
      const response = await saveSettings({
        variables: {
          input: {
            userSettings: [
              {
                kind: UserSettingKind.DashboardSidebar,
                value: JSON.stringify(newSettings),
              },
            ],
          },
        },
      });

      if (printGraphqlErrors(response?.data?.userSettingUpdate?.errors)) {
        return;
      }

      dispatch(setDashboardSidebarSetting(newSettings));
    }
  };

  return (
    <Container>
      <StyledNavItem>
        {headerComponent}

        <KubitTooltip
          noOpenDelay={true}
          content={isExpanded ? 'Collapse' : 'Expand'}
          placement="bottom"
        >
          <Title {...getToggleProps({ onClick: onToggleSection })}>
            {title}
          </Title>
        </KubitTooltip>

        {actions}
      </StyledNavItem>
      <CollapseContent {...getCollapseProps()}>{children}</CollapseContent>
    </Container>
  );
};

export default CollapseSection;
